import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, ColumnApi, GridReadyEvent, CellValueChangedEvent, IFloatingFilter, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AccountEntry, CategoryTemplate } from '../../types';
import { createPeriodToDateMapping } from '../../lib/utils/dateMapping';
import { EditModeToggle } from './EditModeToggle';
import { Filter, RotateCcw, Undo, Redo, X, Plus, Trash2, ArrowUpDown, CalendarPlus, CalendarX2 } from 'lucide-react';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface AccountsTableAGProps {
  accounts: AccountEntry[];
  activeTemplate?: CategoryTemplate | null;
  onUploadAdditionalData?: () => void;
  mappings?: { [accountNumber: string]: { accountNumber: string; name: string; category: string } };
  onAccountsChange?: (accounts: AccountEntry[]) => void;
}

interface AccountGroup {
  accountNumber: string;
  name: string;
  category: string | undefined;
  originalOrder: number;
  [key: string]: any; // For dynamic month columns
}

export function AccountsTableAG({ 
  accounts, 
  activeTemplate, 
  onUploadAdditionalData, 
  mappings = {},
  onAccountsChange 
}: AccountsTableAGProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [undoStack, setUndoStack] = useState<any[]>([]);
  const [redoStack, setRedoStack] = useState<any[]>([]);
  const [lastChangeMessage, setLastChangeMessage] = useState<string>('');
  const [showChangeMessage, setShowChangeMessage] = useState(false);
  const [isUndoRedoInProgress, setIsUndoRedoInProgress] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const [selectedRowCount, setSelectedRowCount] = useState(0);
  const [showAddPeriod, setShowAddPeriod] = useState(false);
  const [newPeriodValue, setNewPeriodValue] = useState('');
  const [showDeletePeriod, setShowDeletePeriod] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState('');
  const gridRef = useRef<AgGridReact>(null);

  // Get all existing account numbers for uniqueness validation
  const existingAccountNumbers = useMemo(() => {
    return new Set(accounts.map(a => a.accountNumber));
  }, [accounts]);

  // Get category display name from template (with mappings priority)
  const getCategoryDisplayName = (accountNumber: string, fallbackCategory: string | undefined) => {
    const categoryValue = mappings[accountNumber]?.category || fallbackCategory || '';
    if (!categoryValue || !activeTemplate) return categoryValue || '';
    
    const templateCategory = activeTemplate.categories.find(
      cat => cat.value === categoryValue
    );
    
    return templateCategory?.label || categoryValue;
  };

  // Extract all unique months from the data, sorted chronologically
  // Keep months for business logic (filtering, grouping) but get dates for display
  const months = useMemo(() => {
    const allMonths = [...new Set(accounts.map(a => a.month))];
    
    // Sort months chronologically using existing logic
    return allMonths.sort((a, b) => {
      const [aMonth, aYear] = a.split('-');
      const [bMonth, bYear] = b.split('-');
      
      // Compare years first
      if (aYear !== bYear) {
        return parseInt(aYear) - parseInt(bYear);
      }
      
      // If years are the same, compare months
      const monthOrder: Record<string, number> = {
        'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
        'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
      };
      
      return monthOrder[aMonth] - monthOrder[bMonth];
    });
  }, [accounts]);

  // Create mapping from month to actual backend date values
  const monthToDateMap = useMemo(() => createPeriodToDateMapping(accounts), [accounts]);

  // Get the actual date for header display (use existing date field from backend)
  const getDateForHeader = useCallback((month: string) => {
    // Use the actual date field from the backend data
    return monthToDateMap[month] || month; // fallback to month if no date found
  }, [monthToDateMap]);

  // Group accounts by accountNumber and name
  const groupedAccounts = useMemo(() => {
    const groupedData: Record<string, AccountGroup> = {};
    
    // First pass: establish original order based on first occurrence of each account
    const seenAccounts = new Set<string>();
    let orderIndex = 0;
    
    accounts.forEach(account => {
      const key = `${account.accountNumber}-${account.name}`;
      
      if (!seenAccounts.has(key)) {
        seenAccounts.add(key);
        groupedData[key] = {
          accountNumber: account.accountNumber,
          name: account.name,
          category: mappings[account.accountNumber]?.category || account.category,
          originalOrder: orderIndex++
        };
      }
    });
    
    // Second pass: populate monthly balances
    accounts.forEach(account => {
      const key = `${account.accountNumber}-${account.name}`;
      const netDebit = account.debit - account.credit;
      
      groupedData[key][account.month] = netDebit;
      // Ensure category is updated
      groupedData[key].category = mappings[account.accountNumber]?.category || account.category;
    });
    
    return Object.values(groupedData);
  }, [accounts, mappings]);

  // Format currency values
  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Value formatter for currency columns
  const currencyFormatter = useCallback((params: any) => {
    if (params.value == null || params.value === undefined) return '';
    return formatCurrency(params.value);
  }, [formatCurrency]);

  // Value parser for currency columns
  const currencyParser = useCallback((params: any) => {
    const value = params.newValue;
    if (value === '' || value == null) return null;
    const parsed = parseFloat(String(value).replace(/[$,\s]/g, ''));
    return isNaN(parsed) ? null : parsed;
  }, []);

  // Define column definitions
  const columnDefs = useMemo((): ColDef[] => {
    const staticColumns: ColDef[] = [
      {
        field: 'originalOrder',
        colId: 'originalOrder',
        hide: true,
        sortable: true
      },
      {
        colId: 'checkbox',
        headerName: '',
        pinned: 'left',
        width: 50,
        maxWidth: 50,
        checkboxSelection: (params) => !params.data?.isTotal,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        editable: false,
        filter: false,
        sortable: false,
        suppressMovable: true,
        resizable: false,
        lockPosition: 'left' as const
      },
      {
        field: 'accountNumber',
        headerName: 'Account Number',
        pinned: 'left',
        width: 130,
        editable: (params) => isEditMode && !params.data?.isTotal,
        filter: 'agTextColumnFilter',
        floatingFilter: showFilters,
        sortable: true,
        filterParams: {
          suppressAndOrCondition: true
        },
        cellEditor: 'agTextCellEditor',
        valueSetter: (params) => {
          const newAccNum = params.newValue?.toString().trim() || '';
          const oldAccNum = params.oldValue?.toString().trim() || '';

          if (!newAccNum) {
            setValidationError('Account number cannot be empty.');
            setTimeout(() => setValidationError(''), 3000);
            return false;
          }

          // Check uniqueness (allow keeping the same value)
          if (newAccNum !== oldAccNum) {
            const otherAccountNumbers = new Set(
              groupedAccounts
                .filter(a => a.accountNumber !== oldAccNum)
                .map(a => a.accountNumber)
            );
            if (otherAccountNumbers.has(newAccNum)) {
              setValidationError(`Account number "${newAccNum}" already exists. Account numbers must be unique.`);
              setTimeout(() => setValidationError(''), 4000);
              return false;
            }
          }

          params.data.accountNumber = newAccNum;
          return true;
        }
      },
      {
        field: 'name',
        headerName: 'Account Name',
        pinned: 'left',
        width: 200,
        editable: (params) => isEditMode && !params.data?.isTotal,
        filter: 'agTextColumnFilter',
        floatingFilter: showFilters,
        sortable: true,
        filterParams: {
          suppressAndOrCondition: true
        },
        cellEditor: 'agTextCellEditor'
      },
      { 
        field: 'category', 
        headerName: 'Category', 
        width: 150,
        editable: false,
        filter: 'agTextColumnFilter',
        floatingFilter: showFilters,
        sortable: true,
        filterParams: {
          suppressAndOrCondition: true
        },
        valueGetter: (params) => {
          return getCategoryDisplayName(params.data.accountNumber, params.data.category);
        }
      }
    ];

    const monthColumns: ColDef[] = months.map(month => ({
      field: month,
      headerName: getDateForHeader(month),
      width: 120,
      editable: (params) => isEditMode && !params.data?.isTotal,
      type: 'numericColumn',
      cellClass: (params) => {
        const value = params.value || 0;
        return value < 0 ? 'text-red-600 text-right' : 'text-gray-900 text-right';
      },
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-right-aligned-header',
      valueFormatter: currencyFormatter,
      valueParser: currencyParser,
      filter: 'agNumberColumnFilter',
      floatingFilter: showFilters,
      sortable: true,
      filterParams: {
        suppressAndOrCondition: true
      },
      // Excel-like cell editing behavior
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 50
      }
    }));

    return [...staticColumns, ...monthColumns];
  }, [months, isEditMode, showFilters, getCategoryDisplayName, getDateForHeader, currencyFormatter, currencyParser]);

  // Calculate monthly totals for the totals row
  const monthlyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    
    months.forEach(month => {
      totals[month] = 0;
    });
    
    groupedAccounts.forEach(account => {
      months.forEach(month => {
        totals[month] += account[month] || 0;
      });
    });
    
    return totals;
  }, [groupedAccounts, months]);

  // Separate row data from pinned bottom row data
  const rowData = useMemo(() => {
    return [...groupedAccounts];
  }, [groupedAccounts]);

  // Create pinned bottom row for totals
  const pinnedBottomRowData = useMemo(() => {
    const totalsRow: any = {
      accountNumber: 'TOTAL',
      name: 'Total (Check)',
      category: '',
      originalOrder: 999999,
      isTotal: true
    };

    months.forEach(month => {
      totalsRow[month] = monthlyTotals[month];
    });

    return [totalsRow];
  }, [months, monthlyTotals]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
  }, []);

  const onCellValueChanged = useCallback((params: CellValueChangedEvent) => {
    if (!onAccountsChange) return;
    
    // Skip undo/redo tracking during programmatic undo/redo operations
    if (isUndoRedoInProgress) return;
    
    // Store the old value for undo functionality
    const oldValue = params.oldValue;
    const newValue = params.newValue;
    
    // Create undo record
    const undoRecord = {
      rowIndex: params.rowIndex,
      colId: params.colDef.field,
      oldValue: oldValue,
      newValue: newValue,
      accountNumber: params.data.accountNumber,
      accountName: params.data.name,
      timestamp: new Date()
    };
    
    // Add to undo stack and clear redo stack
    setUndoStack(prev => [...prev, undoRecord]);
    setRedoStack([]);
    
    // Show user feedback
    const changeMessage = `Updated ${params.data.name} (${params.colDef.headerName}): ${oldValue || 0} → ${newValue || 0}`;
    setLastChangeMessage(changeMessage);
    setShowChangeMessage(true);
    
    // Hide message after 3 seconds
    setTimeout(() => setShowChangeMessage(false), 3000);
    
    console.log('Cell value changed:', changeMessage);
    
    const changedField = params.colDef.field;
    let updatedAccounts: AccountEntry[];

    if (changedField === 'accountNumber') {
      // Account number changed - update all entries with the old account number
      updatedAccounts = accounts.map(account => {
        if (account.accountNumber === oldValue && account.name === params.data.name) {
          return { ...account, accountNumber: newValue };
        }
        return account;
      });
    } else if (changedField === 'name') {
      // Account name changed - update all entries with this account number
      updatedAccounts = accounts.map(account => {
        if (account.accountNumber === params.data.accountNumber && account.name === oldValue) {
          return { ...account, name: newValue };
        }
        return account;
      });
    } else {
      // Month value changed - update the specific month entry
      updatedAccounts = accounts.map(account => {
        if (account.accountNumber === params.data.accountNumber &&
            account.name === params.data.name &&
            account.month === changedField) {

          const finalNewValue = newValue || 0;
          if (finalNewValue >= 0) {
            return { ...account, debit: finalNewValue, credit: 0 };
          } else {
            return { ...account, debit: 0, credit: Math.abs(finalNewValue) };
          }
        }
        return account;
      });
    }

    onAccountsChange(updatedAccounts);
  }, [accounts, onAccountsChange, isUndoRedoInProgress]);

  const toggleEditMode = useCallback(() => {
    setIsEditMode(!isEditMode);
  }, [isEditMode]);

  const onSelectionChanged = useCallback(() => {
    if (gridApi) {
      setSelectedRowCount(gridApi.getSelectedNodes().length);
    }
  }, [gridApi]);

  // Add a new blank row (creates AccountEntry records for every month)
  const addNewRow = useCallback(() => {
    if (!onAccountsChange || months.length === 0) return;

    // Generate a unique placeholder account number
    let newAccountNumber = '';
    let counter = 1;
    const allAccountNumbers = new Set(accounts.map(a => a.accountNumber));
    do {
      newAccountNumber = `NEW-${String(counter).padStart(3, '0')}`;
      counter++;
    } while (allAccountNumbers.has(newAccountNumber));

    // Use the date from the first existing account for each month as reference
    const monthDateMap: Record<string, string> = {};
    accounts.forEach(a => {
      if (!monthDateMap[a.month]) {
        monthDateMap[a.month] = a.date;
      }
    });

    // Create one AccountEntry per month
    const newEntries: AccountEntry[] = months.map(month => ({
      name: 'New Account',
      accountNumber: newAccountNumber,
      month,
      date: monthDateMap[month] || month,
      debit: 0,
      credit: 0,
      category: undefined
    }));

    const updatedAccounts = [...accounts, ...newEntries];
    onAccountsChange(updatedAccounts);

    // Show feedback
    setLastChangeMessage(`Added new account row "${newAccountNumber}". Click the account number or name to edit.`);
    setShowChangeMessage(true);
    setTimeout(() => setShowChangeMessage(false), 4000);

    // Scroll to the new row after grid updates
    setTimeout(() => {
      if (gridApi) {
        const lastRowIndex = gridApi.getDisplayedRowCount() - 1; // last regular row (pinned bottom rows not included in count)
        if (lastRowIndex >= 0) {
          gridApi.ensureIndexVisible(lastRowIndex, 'bottom');
          // Focus the account number cell of the new row for immediate editing
          gridApi.setFocusedCell(lastRowIndex, 'accountNumber');
          gridApi.startEditingCell({
            rowIndex: lastRowIndex,
            colKey: 'accountNumber'
          });
        }
      }
    }, 100);
  }, [accounts, months, onAccountsChange, gridApi]);

  // Add a new time period (month column)
  const addNewPeriod = useCallback(() => {
    if (!onAccountsChange || !newPeriodValue) return;

    // newPeriodValue is in "YYYY-MM" format from the month input
    const [yearStr, monthNumStr] = newPeriodValue.split('-');
    const year = parseInt(yearStr);
    const monthNum = parseInt(monthNumStr);

    if (isNaN(year) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      setValidationError('Invalid date. Please select a valid month.');
      setTimeout(() => setValidationError(''), 3000);
      return;
    }

    // Build the month key in "Mon-YYYY" format
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthKey = `${monthNames[monthNum - 1]}-${year}`;

    // Check for duplicate
    if (months.includes(monthKey)) {
      setValidationError(`Period "${monthNames[monthNum - 1]} ${year}" already exists.`);
      setTimeout(() => setValidationError(''), 4000);
      return;
    }

    // Calculate end-of-month date for the date field (MM/DD/YYYY)
    const lastDay = new Date(year, monthNum, 0).getDate();
    const dateStr = `${String(monthNum).padStart(2, '0')}/${String(lastDay).padStart(2, '0')}/${year}`;

    // Get unique accounts (by accountNumber + name)
    const uniqueAccounts = new Map<string, { accountNumber: string; name: string; category?: string }>();
    accounts.forEach(a => {
      const key = `${a.accountNumber}|||${a.name}`;
      if (!uniqueAccounts.has(key)) {
        uniqueAccounts.set(key, { accountNumber: a.accountNumber, name: a.name, category: a.category });
      }
    });

    // Create one AccountEntry per unique account for the new month
    const newEntries: AccountEntry[] = Array.from(uniqueAccounts.values()).map(acc => ({
      name: acc.name,
      accountNumber: acc.accountNumber,
      month: monthKey,
      date: dateStr,
      debit: 0,
      credit: 0,
      category: acc.category
    }));

    const updatedAccounts = [...accounts, ...newEntries];
    onAccountsChange(updatedAccounts);

    // Reset picker
    setShowAddPeriod(false);
    setNewPeriodValue('');

    setLastChangeMessage(`Added new period: ${monthNames[monthNum - 1]} ${year}`);
    setShowChangeMessage(true);
    setTimeout(() => setShowChangeMessage(false), 3000);
  }, [newPeriodValue, accounts, months, onAccountsChange]);

  // Delete an existing time period (month column)
  const deletePeriod = useCallback(() => {
    if (!onAccountsChange || !periodToDelete) return;

    if (!months.includes(periodToDelete)) {
      setValidationError('Selected period no longer exists.');
      setTimeout(() => setValidationError(''), 3000);
      return;
    }

    if (months.length <= 1) {
      setValidationError('Cannot delete the only remaining period.');
      setTimeout(() => setValidationError(''), 3000);
      return;
    }

    const updatedAccounts = accounts.filter(account => account.month !== periodToDelete);
    onAccountsChange(updatedAccounts);

    const displayLabel = getDateForHeader(periodToDelete);
    setShowDeletePeriod(false);
    setPeriodToDelete('');

    setLastChangeMessage(`Deleted period: ${displayLabel}`);
    setShowChangeMessage(true);
    setTimeout(() => setShowChangeMessage(false), 3000);
  }, [accounts, months, onAccountsChange, periodToDelete, getDateForHeader]);

  // Delete selected rows
  const deleteSelectedRows = useCallback(() => {
    if (!gridApi || !onAccountsChange) return;

    const selectedNodes = gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return;

    // Collect account keys (accountNumber + name) to delete
    const keysToDelete = new Set(
      selectedNodes.map(node => `${node.data.accountNumber}|||${node.data.name}`)
    );

    const updatedAccounts = accounts.filter(
      account => !keysToDelete.has(`${account.accountNumber}|||${account.name}`)
    );

    onAccountsChange(updatedAccounts);
    gridApi.deselectAll();

    const count = selectedNodes.length;
    setLastChangeMessage(`Deleted ${count} account${count > 1 ? 's' : ''}.`);
    setShowChangeMessage(true);
    setTimeout(() => setShowChangeMessage(false), 3000);
  }, [gridApi, accounts, onAccountsChange]);

  // Switch signs for all values on selected rows
  const switchSignsSelectedRows = useCallback(() => {
    if (!gridApi || !onAccountsChange) return;

    const selectedNodes = gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return;

    const keysToSwitch = new Set(
      selectedNodes.map(node => `${node.data.accountNumber}|||${node.data.name}`)
    );

    const updatedAccounts = accounts.map(account => {
      if (keysToSwitch.has(`${account.accountNumber}|||${account.name}`)) {
        return {
          ...account,
          debit: account.credit,
          credit: account.debit
        };
      }
      return account;
    });

    onAccountsChange(updatedAccounts);
    gridApi.deselectAll();

    const count = selectedNodes.length;
    setLastChangeMessage(`Switched signs for ${count} account${count > 1 ? 's' : ''}.`);
    setShowChangeMessage(true);
    setTimeout(() => setShowChangeMessage(false), 3000);
  }, [gridApi, accounts, onAccountsChange]);

  const toggleFilters = useCallback(() => {
    setShowFilters(!showFilters);
    // Refresh columns to show/hide floating filters
    setTimeout(() => {
      if (columnApi) {
        columnApi.resetColumnState();
      }
    }, 100);
  }, [showFilters, columnApi]);

  const clearFilters = useCallback(() => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      setHasActiveFilters(false);
    }
  }, [gridApi]);

  const resetToOriginalOrder = useCallback(() => {
    if (gridApi) {
      try {
        // In AG Grid v34+, use gridApi.applyColumnState directly
        gridApi.applyColumnState({
          state: [{ colId: 'originalOrder', sort: 'asc' }],
          defaultState: { sort: null }
        });
        console.log('Reset order applied successfully');
      } catch (error) {
        console.error('Failed to reset order:', error);
        // Fallback: try via columnApi if it exists
        if (columnApi && typeof columnApi.applyColumnState === 'function') {
          try {
            columnApi.applyColumnState({
              state: [{ colId: 'originalOrder', sort: 'asc' }],
              defaultState: { sort: null }
            });
            console.log('Reset order applied via columnApi fallback');
          } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError);
          }
        }
      }
    }
  }, [gridApi, columnApi]);

  // Monitor filter changes with cleanup protection
  useEffect(() => {
    if (gridApi) {
      const onFilterChanged = () => {
        const filterModel = gridApi.getFilterModel();
        const hasFilters = Object.keys(filterModel).length > 0;
        setHasActiveFilters(hasFilters);
      };
      
      gridApi.addEventListener('filterChanged', onFilterChanged);
      return () => {
        // Check if grid is destroyed before removing listener
        if (gridApi && !gridApi.isDestroyed()) {
          gridApi.removeEventListener('filterChanged', onFilterChanged);
        }
      };
    }
  }, [gridApi]);

  // Custom Undo/Redo functions with user feedback
  const undo = useCallback(() => {
    if (undoStack.length === 0) {
      console.log('Nothing to undo');
      setLastChangeMessage('Nothing to undo');
      setShowChangeMessage(true);
      setTimeout(() => setShowChangeMessage(false), 2000);
      return;
    }
    
    const lastChange = undoStack[undoStack.length - 1];
    console.log('Undoing change:', lastChange);
    
    // Find and update the cell value
    if (gridApi) {
      const rowNode = gridApi.getDisplayedRowAtIndex(lastChange.rowIndex);
      if (rowNode && rowNode.data) {
        // Set flag to prevent recursive undo tracking
        setIsUndoRedoInProgress(true);
        
        // Update the grid data directly
        rowNode.setDataValue(lastChange.colId, lastChange.oldValue);
        
        // Move from undo stack to redo stack (batch state updates)
        const newUndoStack = undoStack.slice(0, -1);
        const newRedoStack = [...redoStack, lastChange];
        
        console.log('Undo: updating stacks - undo length:', newUndoStack.length, 'redo length:', newRedoStack.length);
        setUndoStack(newUndoStack);
        setRedoStack(newRedoStack);
        
        // Clear the flag after a brief delay to ensure state updates complete
        setTimeout(() => setIsUndoRedoInProgress(false), 50);
        
        // Show feedback
        const undoMessage = `Undone: ${lastChange.accountName} (${lastChange.colId}): ${lastChange.newValue || 0} → ${lastChange.oldValue || 0}`;
        setLastChangeMessage(undoMessage);
        setShowChangeMessage(true);
        setTimeout(() => setShowChangeMessage(false), 3000);
        
        console.log('Undo completed:', undoMessage);
      }
    }
  }, [undoStack, gridApi]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) {
      console.log('Nothing to redo');
      setLastChangeMessage('Nothing to redo');
      setShowChangeMessage(true);
      setTimeout(() => setShowChangeMessage(false), 2000);
      return;
    }
    
    const lastUndo = redoStack[redoStack.length - 1];
    console.log('Redoing change:', lastUndo);
    
    // Find and update the cell value
    if (gridApi) {
      const rowNode = gridApi.getDisplayedRowAtIndex(lastUndo.rowIndex);
      if (rowNode && rowNode.data) {
        // Set flag to prevent recursive undo tracking
        setIsUndoRedoInProgress(true);
        
        // Update the grid data directly
        rowNode.setDataValue(lastUndo.colId, lastUndo.newValue);
        
        // Move from redo stack to undo stack (batch state updates)
        const newRedoStack = redoStack.slice(0, -1);
        const newUndoStack = [...undoStack, lastUndo];
        
        console.log('Redo: updating stacks - redo length:', newRedoStack.length, 'undo length:', newUndoStack.length);
        setRedoStack(newRedoStack);
        setUndoStack(newUndoStack);
        
        // Clear the flag after a brief delay to ensure state updates complete
        setTimeout(() => setIsUndoRedoInProgress(false), 50);
        
        // Show feedback
        const redoMessage = `Redone: ${lastUndo.accountName} (${lastUndo.colId}): ${lastUndo.oldValue || 0} → ${lastUndo.newValue || 0}`;
        setLastChangeMessage(redoMessage);
        setShowChangeMessage(true);
        setTimeout(() => setShowChangeMessage(false), 3000);
        
        console.log('Redo completed:', redoMessage);
      }
    }
  }, [redoStack, gridApi]);

  // Enhanced keyboard shortcuts with grid focus detection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the grid container is focused or contains the active element
      const gridContainer = document.querySelector('.ag-grid-container');
      const agGridElement = document.querySelector('.ag-root-wrapper');
      const isGridFocused = (gridContainer && (
        gridContainer.contains(document.activeElement) || 
        document.activeElement?.closest('.ag-grid-container')
      )) || (agGridElement && agGridElement.contains(document.activeElement));

      // Let AG Grid handle all tab navigation through tabToNextCell

      if (!isGridFocused) return;

      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      if (isCtrlOrCmd) {
        switch (event.key.toLowerCase()) {
          case 'z':
            if (!event.shiftKey) {
              // Undo
              event.preventDefault();
              undo();
              console.log('Undo triggered via keyboard');
            } else {
              // Redo (Ctrl+Shift+Z)
              event.preventDefault();
              redo();
              console.log('Redo triggered via keyboard (Ctrl+Shift+Z)');
            }
            break;
          case 'y':
            // Redo (Ctrl+Y)
            event.preventDefault();
            redo();
            console.log('Redo triggered via keyboard (Ctrl+Y)');
            break;
          case 'c':
            // Copy - let AG Grid handle this natively, just log
            console.log('Copy triggered via keyboard (AG Grid native)');
            break;
          case 'v':
            // Paste - let AG Grid handle this natively, just log
            if (isEditMode) {
              console.log('Paste triggered via keyboard (AG Grid native)');
            } else {
              event.preventDefault();
              console.log('Paste blocked - not in edit mode');
            }
            break;
          case 'e':
            // Toggle edit mode
            event.preventDefault();
            setIsEditMode(!isEditMode);
            console.log('Edit mode toggled via keyboard');
            break;
          case 'a':
            // Select all - let AG Grid handle this
            if (isEditMode) {
              console.log('Select all triggered (AG Grid native)');
            }
            break;
        }
      }
    };

    // Attach to document but check for grid focus
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditMode, undo, redo]);

  const getRowClass = useCallback((params: any) => {
    if (params.data.isTotal) {
      return 'ag-row-total font-semibold bg-gray-50 border-t-2 border-gray-300';
    }
    return '';
  }, []);

  // Default grid options (Community edition compatible)
  const gridOptions = {
    // Basic Community features only - avoid all Enterprise/deprecated options
    enableCellTextSelection: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 50,
    
    // Excel-like editing behavior
    singleClickEdit: true, // Single click to start editing
    stopEditingWhenCellsLoseFocus: true, // Auto-commit when clicking outside
    enterNavigatesVertically: true, // Enter moves to next row
    enterNavigatesVerticallyAfterEdit: true, // Enter commits and moves down
    
    // Enhanced tab navigation (Excel-like) - use AG Grid's built-in tabToNextCell
    tabToNextCell: (params: any) => {
      const { backwards, editing, previousCellPosition } = params;
      
      if (!isEditMode) {
        return false; // Let browser handle tab when not in edit mode
      }
      
      const allColumns = columnApi?.getAllDisplayedColumns() || [];
      const editableColumns = allColumns.filter(col => col.getColDef().editable);
      
      if (editableColumns.length === 0) {
        return false;
      }
      
      const currentRowIndex = previousCellPosition.rowIndex;
      const currentColId = previousCellPosition.column.getColId();
      const currentColIndex = editableColumns.findIndex(col => col.getColId() === currentColId);
      
      if (backwards) {
        // Shift+Tab - move to previous editable cell
        if (currentColIndex > 0) {
          // Move to previous column in same row
          return {
            rowIndex: currentRowIndex,
            column: editableColumns[currentColIndex - 1]
          };
        } else if (currentRowIndex > 0) {
          // Move to last editable column of previous row
          return {
            rowIndex: currentRowIndex - 1,
            column: editableColumns[editableColumns.length - 1]
          };
        }
        return null; // Stay on current cell if at beginning
      } else {
        // Tab - move to next editable cell
        if (currentColIndex < editableColumns.length - 1) {
          // Move to next column in same row
          return {
            rowIndex: currentRowIndex,
            column: editableColumns[currentColIndex + 1]
          };
        } else if (currentRowIndex < (gridApi?.getDisplayedRowCount() || 1) - 1) {
          // Move to first editable column of next row (skip totals row)
          const nextRowIndex = currentRowIndex + 1;
          const nextRowNode = gridApi?.getDisplayedRowAtIndex(nextRowIndex);
          if (nextRowNode && !nextRowNode.data?.isTotal) {
            return {
              rowIndex: nextRowIndex,
              column: editableColumns[0]
            };
          }
        }
        return null; // Stay on current cell if at end
      }
    },
    
    // Grid layout and interaction
    rowHeight: 40,
    headerHeight: showFilters ? 65 : 45,
    suppressHorizontalScroll: false,
    alwaysShowHorizontalScroll: false,
    suppressMenuHide: true,
    getRowClass: getRowClass,
    
    // Row selection via checkbox only
    rowSelection: 'multiple' as const,
    suppressRowClickSelection: true,
    suppressCellFocus: false,
    
    // Enhanced clipboard processing
    processCellForClipboard: (params: any) => {
      // Return formatted value for copying
      if (params.column.getColId() !== 'accountNumber' && 
          params.column.getColId() !== 'name' && 
          params.column.getColId() !== 'category') {
        // For numeric columns, return the raw number
        return params.value || 0;
      }
      return params.value || '';
    },
    
    processCellFromClipboard: (params: any) => {
      // Process pasted values
      const value = params.value;
      if (!value || value.trim() === '') return null;
      
      // For numeric columns, parse the value
      if (params.column.getColId() !== 'accountNumber' && 
          params.column.getColId() !== 'name' && 
          params.column.getColId() !== 'category') {
        const parsed = parseFloat(String(value).replace(/[$,\s]/g, ''));
        return isNaN(parsed) ? 0 : parsed;
      }
      return value;
    },
    
    // Enhanced editing events
    onCellEditingStarted: (params: any) => {
      console.log('Cell editing started:', params.colDef.headerName);
    },
    
    onCellEditingStopped: (params: any) => {
      console.log('Cell editing stopped:', params.colDef.headerName);
    },
    
    // Handle cell clicks for better focus management
    onCellClicked: (params: any) => {
      // Ensure grid gets focus when clicked
      const gridElement = document.querySelector('.ag-root-wrapper') as HTMLElement;
      if (gridElement && !gridElement.contains(document.activeElement)) {
        gridElement.focus();
      }
    },
    
    // Handle cell focus for tab navigation
    onCellFocused: (params: any) => {
      // Store the focused cell for potential tab navigation
      console.log('Cell focused:', params.column?.getColId(), 'Row:', params.rowIndex);
    }
  };

  return (
    <div className="mt-8 flow-root">
      {/* Active Template Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
              Active Template:
            </span>
            <span className="text-sm text-blue-800 font-medium">
              {activeTemplate?.name || 'None'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <EditModeToggle
              isEditMode={isEditMode}
              onToggle={toggleEditMode}
              disabled={accounts.length === 0}
            />
            {isEditMode && (
              <>
                <button
                  onClick={addNewRow}
                  className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Row
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowAddPeriod(!showAddPeriod)}
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      showAddPeriod
                        ? 'bg-purple-700 text-white'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    <CalendarPlus className="w-4 h-4 mr-1.5" />
                    Add Period
                  </button>
                  {showAddPeriod && (
                    <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-64">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Month
                      </label>
                      <input
                        type="month"
                        value={newPeriodValue}
                        onChange={(e) => setNewPeriodValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={addNewPeriod}
                          disabled={!newPeriodValue}
                          className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            newPeriodValue
                              ? 'bg-purple-600 text-white hover:bg-purple-700'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Add
                        </button>
                        <button
                          onClick={() => { setShowAddPeriod(false); setNewPeriodValue(''); }}
                          className="flex-1 px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={() => {
                      const opening = !showDeletePeriod;
                      setShowDeletePeriod(opening);
                      if (opening && !periodToDelete && months.length > 0) {
                        setPeriodToDelete(months[months.length - 1]);
                      }
                    }}
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      showDeletePeriod
                        ? 'bg-red-700 text-white'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                    disabled={months.length === 0}
                  >
                    <CalendarX2 className="w-4 h-4 mr-1.5" />
                    Delete Period
                  </button>
                  {showDeletePeriod && (
                    <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-72">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Period to Delete
                      </label>
                      <select
                        value={periodToDelete}
                        onChange={(e) => setPeriodToDelete(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="" disabled>
                          Choose a period
                        </option>
                        {months.map(month => (
                          <option key={month} value={month}>
                            {getDateForHeader(month)} ({month})
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-gray-500">
                        This removes the selected period for all accounts.
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={deletePeriod}
                          disabled={!periodToDelete || months.length <= 1}
                          className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            periodToDelete && months.length > 1
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => {
                            setShowDeletePeriod(false);
                            setPeriodToDelete('');
                          }}
                          className="flex-1 px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              onClick={onUploadAdditionalData}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Upload Additional Data
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {groupedAccounts.length} unique accounts across {months.length} months
          {isEditMode && (
            <span className="ml-2 text-orange-600 font-medium">
              • Edit Mode Active | Click any cell to edit | Tab/Enter to navigate | Ctrl+Z/Y for Undo/Redo
            </span>
          )}
          {hasActiveFilters && (
            <span className="ml-2 text-blue-600 font-medium">• Filters Applied</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isEditMode && (
            <>
              <div className="flex items-center gap-1 border-r pr-3">
                <button
                  onClick={() => {
                    console.log('Undo clicked - undoStack length:', undoStack.length, 'redoStack length:', redoStack.length);
                    undo();
                  }}
                  disabled={undoStack.length === 0}
                  className={`inline-flex items-center px-3 py-1.5 text-sm rounded-md ${
                    undoStack.length === 0 
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title={`Undo (Ctrl+Z) ${undoStack.length > 0 ? `- ${undoStack.length} changes available` : '- No changes to undo'}`}
                >
                  <Undo className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    console.log('Redo clicked - undoStack length:', undoStack.length, 'redoStack length:', redoStack.length);
                    redo();
                  }}
                  disabled={redoStack.length === 0}
                  className={`inline-flex items-center px-3 py-1.5 text-sm rounded-md ${
                    redoStack.length === 0 
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title={`Redo (Ctrl+Y) ${redoStack.length > 0 ? `- ${redoStack.length} changes available` : '- No changes to redo'}`}
                >
                  <Redo className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
          
          <button
            onClick={toggleFilters}
            className={`inline-flex items-center px-3 py-1.5 text-sm rounded-md transition-colors ${
              showFilters 
                ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-md"
            >
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </button>
          )}
          
          <button
            onClick={resetToOriginalOrder}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Order
          </button>
        </div>
      </div>

      {/* Selection Actions Bar */}
      {selectedRowCount > 0 && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-indigo-800 font-medium">
              {selectedRowCount} row{selectedRowCount > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={switchSignsSelectedRows}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-md font-medium transition-colors"
              >
                <ArrowUpDown className="w-4 h-4 mr-1.5" />
                Switch Signs
              </button>
              <button
                onClick={deleteSelectedRows}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-md font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Delete Row{selectedRowCount > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Error */}
      {validationError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-400 rounded-full mr-3"></div>
            <span className="text-sm text-red-800 font-medium">{validationError}</span>
          </div>
        </div>
      )}

      {/* Change Notification */}
      {showChangeMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
            <span className="text-sm text-green-800 font-medium">{lastChangeMessage}</span>
          </div>
        </div>
      )}

      {/* AG Grid Container */}
      <div className="ag-theme-alpine ag-grid-container" style={{ height: 'calc(100vh - 320px)', width: '100%' }} tabIndex={0}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          pinnedBottomRowData={pinnedBottomRowData}
          columnDefs={columnDefs}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          onSelectionChanged={onSelectionChanged}
          animateRows={true}
          enableCellTextSelection={true}
          suppressClipboardPaste={!isEditMode}
          suppressClipboardApi={false}
          theme="legacy"
        />
      </div>

    </div>
  );
}